/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  BuilderFormSchemaFactory,
  BuilderUserContext,
  FormRootSchema,
  FormStateSchema,
  whenAuOrNzRegion,
  whenAuRegion,
  whenCreateOrEditMode,
  whenUsOrAuRegion,
} from '@gh/builder-shared';
import { FormState, MaskedTypes } from '@gh/common-shared';
import { HlpDetails, SystemRoleBit } from '@gh/builder-shared-data';
import { HlpDetailsBusinessService } from '../../resources/hlp-details-business.service';
import { ValidatorFn, Validators } from '@angular/forms';
import { latitudeValidator, longitudeValidator } from '@gh/core-ui';

@Injectable()
export class HlpDetailsFormState extends FormState<HlpDetails> {
  descriptionMaxLength = 2500;
  constructor(
    private businessService: HlpDetailsBusinessService,
    private userContext: BuilderUserContext,
    schemaFactory: BuilderFormSchemaFactory,
  ) {
    super(schemaFactory);
  }

  googleMapUrlValidator(): ValidatorFn {
    return (control): { [key: string]: any } | null => {
      const value = control.value;
      const maxLength = 500;
      const patternGoogleUrl = /^https:\/\/www\.google\.com(.[a-z]{2})?\/maps\//;
      const invalid = !patternGoogleUrl.test(value);
      if (!value || !value.length) {
        return { required: true };
      }
      if (value.length > maxLength) {
        return { pattern: { messageId: 'msg_field_maxlength', requiredLength: maxLength } };
      }
      return invalid ? { pattern: { messageId: 'msg_invalid_url' } } : null;
    };
  }

  textWordCountValidator(maxLength: number): ValidatorFn {
    return (control): { [key: string]: any } | null => {  
      const value = control.value;
  
      if (!value) {
        return { required: true };
      }
      const plainText = value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').replace(/\n+/g, ' ').replace(/&amp;/g, '&').trim();
  
      if (plainText.length === 0) {
        return { required: true };
      }
  
      if (plainText.length > maxLength) {
        return { maxlength: {messageId: 'msg_field_maxlength', requiredLength: maxLength, actualLength: plainText.length } }; }
  
      return null;
    };
  }

  protected buildSchema(details: HlpDetails, { root, group }: FormStateSchema): FormRootSchema {
    const isCorpOrMaster = this.userContext.hasSystemRole(SystemRoleBit.Master) || this.userContext.hasSystemRole(SystemRoleBit.Corporate);
    return root({
      optimisticLock: true,
      mode:           this.businessService.getFormMode(details),
      enabled:        whenCreateOrEditMode(),
      children:       {
        // details
        commonDetails: group(
          {
            children: {
              salesperson:                  { required: true },
              alternateSalesperson:         { visible: whenAuOrNzRegion() },
              siteName: { enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked, required: true },
              siteLotNumber: { enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked },
              siteStreet: { enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked, required: true },
              siteStreetNumber: { enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked, required: true },
              siteSuburb: { enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked, required: true },
              sitePostcode:                 {
                visible:  whenUsOrAuRegion(),
                type:     MaskedTypes.Zip,
                enabled:  !details.commonDetails.publishedDate && !details.isSiteDetailsLocked,
                required: true,
              },
              siteState: {
                enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked,
                required: false
              },
              latitude: {
                enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked,
                required: true,
                validators: [latitudeValidator()],
              },
              longitude: {
                enabled: !details.commonDetails.publishedDate && !details.isSiteDetailsLocked,
                required: true,
                validators: [longitudeValidator()],
              },
              isPublishedOnDomainComAu: {
                validators: [
                  (control) => {
                    const formGroup = control.parent;
                    if (!formGroup) return null;
                    const lotNumberControl = formGroup.get('siteLotNumber');
                    if (lotNumberControl) {
                      lotNumberControl.updateValueAndValidity();
                    }
                    return null;
                  }
                ]
              },
              isPublishedOnRealestateComAu: {
                validators: [
                  (control) => {
                    const formGroup = control.parent;
                    if (!formGroup) return null;
                    const lotNumberControl = formGroup.get('siteLotNumber');
                    if (lotNumberControl) {
                      lotNumberControl.updateValueAndValidity();
                    }
                    return null;
                  }
                ]
              },
              isDisplayPriceOverridden:     void 0,
              displayPrice:                 void 0,
              listingStatus:                { visible: whenAuRegion(), required: true },
              memo:                         void 0,
            },
          }),
        // design
        designDetails: group(
          {
            children: {
              baseDesign:             { enabled: !details.commonDetails.publishedDate, required: true },
              designSeriesName:       { enabled: false },
              storeys:                { enabled: isCorpOrMaster, required: true },
              bedrooms:               { enabled: isCorpOrMaster, required: true },
              bathrooms:              { enabled: isCorpOrMaster, required: true },
              carSpaces:              { enabled: isCorpOrMaster, required: true },
              floorArea:              { enabled: isCorpOrMaster, required: true, validators: [Validators.min(0.01)] },
              floorAreaUom:           void 0,
              landArea:               { required: true, validators: [Validators.min(0.1)] },
              landAreaUom:            { required: true },
              websiteDescription:     { required: true,  validators: [
                Validators.required,
                this.textWordCountValidator(this.descriptionMaxLength),
              ]},
              websiteDescriptionText: { validators: [
                Validators.required,
                this.textWordCountValidator(this.descriptionMaxLength),
              ]},
              additionalFeatures:     void 0,
              features:               void 0,
              images:                 void 0,
              facades:                { required: true },
              floorPlans:             void 0,
            },
          }),
        quoteHasChanges:              void 0,
      },
    });
  }
}
